"use client"

import { useCallback } from "react"
import { api } from "@/lib/api"
import { useCurrentUser } from "@/hooks/useCurrentUser"
import {
    isMarkedOpenWallet,
    isMarkedWalletOrg,
    OPEN_WALLET_SNAPSHOT,
    walletWarmPlan,
} from "./walletWarm.utils"

type WalletUtils = ReturnType<typeof api.useUtils>

async function warmWalletQueries({
    utils,
    isOpenWallet,
}: {
    utils: WalletUtils
    isOpenWallet: boolean
}) {
    const onboarding = utils.cartagePay.onboardingState.fetch()
    const plan = walletWarmPlan(
        isOpenWallet ? OPEN_WALLET_SNAPSHOT : await onboarding
    )
    const reads: Promise<unknown>[] = [onboarding]
    if (plan.shouldReadRfis) reads.push(utils.cartagePay.rfis.prefetch())
    if (plan.shouldReadBalances) {
        reads.push(
            utils.cartagePay.walletFunding.prefetch(),
            utils.cartagePay.ledger.prefetch(),
            utils.cartagePay.withdrawals.prefetch()
        )
    }
    if (plan.shouldReadHome) {
        reads.push(
            utils.cartagePay.bills.prefetch(),
            utils.cartagePay.walletSettings.prefetch(),
            utils.cartagePay.missingInvoiceBills.prefetch({ page: 1 }),
            utils.cartagePay.statements.prefetch(),
            utils.cartagePay.currentStatement.prefetch()
        )
    }
    await Promise.all(reads)
}

export function useWarmWallet() {
    const utils = api.useUtils()
    const orgId = useCurrentUser().session?.orgId
    return useCallback(() => {
        if (!orgId || !isMarkedWalletOrg(orgId)) return
        warmWalletQueries({
            utils,
            isOpenWallet: isMarkedOpenWallet(orgId),
        }).catch(() => {
            // Warming is best effort; the window asks again and shows its own error.
        })
    }, [utils, orgId])
}
